import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { Link, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Bell } from 'lucide-react'
import { api } from '../services/api'
import { RootState } from '../store/store'
import { openFoodLogMissWhatsapp } from '../utils/openFoodLogMissWa'

type AdminNote = {
  id: string
  type: string
  title: string
  body: string
  href: string
  readAt: string | null
  createdAt: string
  hasPhone?: boolean
  waUrl?: string | null
}

const TYPE_LABEL: Record<string, string> = {
  new_user: 'Alta',
  note: 'Nota',
  measurement: 'Medidas',
  plan_expiry: 'Plan',
  food_log_miss: 'Recordatorio',
}

function notificationHref(href: string | undefined, role?: string) {
  if (role === 'TRAINER' && (!href || String(href).startsWith('/admin/users'))) return '/admin/vigencias'
  return href || '/admin/users'
}

export default function AdminNotificationsBell({ compact = false }: { compact?: boolean }) {
  const role = useSelector((s: RootState) => s.auth.user?.role)
  const [open, setOpen] = useState(false)
  const [unread, setUnread] = useState(0)
  const [items, setItems] = useState<AdminNote[]>([])
  const wrapRef = useRef<HTMLDivElement>(null)
  const [panelStyle, setPanelStyle] = useState<CSSProperties>({ right: 8, top: 56 })
  const navigate = useNavigate()

  const loadUnread = () => {
    api.get('/admin-notifications/unread-count')
      .then((r) => setUnread(Number(r.data?.unread || 0)))
      .catch(() => {})
  }

  const loadList = () => {
    api.get('/admin-notifications', { params: { limit: 20, unreadOnly: 1 } })
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]))
  }

  useEffect(() => {
    loadUnread()
    const id = window.setInterval(loadUnread, 60000)
    return () => window.clearInterval(id)
  }, [])

  useEffect(() => {
    if (!open) return
    loadList()
    const btn = wrapRef.current?.getBoundingClientRect()
    if (btn) {
      const width = 320
      const left = Math.min(Math.max(8, btn.right - width), window.innerWidth - width - 8)
      setPanelStyle({ position: 'fixed', top: btn.bottom + 8, left, width, zIndex: 80 })
    }
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node
      if (wrapRef.current?.contains(t)) return
      const panel = document.getElementById('food-admin-notif-panel')
      if (panel?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const onOpenItem = async (n: AdminNote) => {
    if (n.type === 'food_log_miss') {
      const ok = await openFoodLogMissWhatsapp(n.id, n.waUrl)
      loadUnread()
      loadList()
      if (ok) setOpen(false)
      return
    }
    if (!n.readAt) {
      await api.patch(`/admin-notifications/${n.id}/read`).catch(() => {})
      setUnread((u) => Math.max(0, u - 1))
    }
    setOpen(false)
    navigate(notificationHref(n.href, role))
  }

  const markAll = async () => {
    await api.post('/admin-notifications/read-all').catch(() => {})
    setUnread(0)
    loadList()
  }

  const iconBtn = compact
    ? 'relative p-1.5 text-gray-600 hover:text-gray-800'
    : 'food-sidebar-nav relative flex-shrink-0 p-1 text-white/70 hover:text-white transition-colors'

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        className={iconBtn}
        aria-label="Notificaciones Food Plan"
        onClick={() => setOpen((v) => !v)}
      >
        <Bell size={compact ? 20 : 18} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center font-bold">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && createPortal(
        <div
          id="food-admin-notif-panel"
          className="food-plan-root rounded-2xl bg-white border border-gray-200 shadow-lg overflow-hidden"
          style={{ ...panelStyle, color: '#111827' }}
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-[#f9fafb]">
            <p className="text-sm font-semibold text-[#111827]">Notificaciones</p>
            {unread > 0 && (
              <button type="button" onClick={markAll} className="text-xs font-medium text-[#64ba30]">
                Marcar leídas
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-3 py-8 text-center">
                <p className="text-sm text-gray-600">No hay avisos nuevos</p>
                <p className="text-xs text-gray-400 mt-1">El historial está en Ver historial. Los de alimentos salen desde las 15:00 (Bogotá) si no registraron hoy.</p>
              </div>
            ) : items.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => onOpenItem(n)}
                className={`w-full text-left px-3 py-2.5 border-b border-gray-50 hover:bg-[#64ba30]/5 ${n.readAt ? '' : 'bg-[#64ba30]/10'}`}
              >
                <span className="text-[10px] font-semibold uppercase tracking-wide text-[#64ba30]">
                  {TYPE_LABEL[n.type] || n.type}
                </span>
                <p className="text-sm font-medium text-[#111827] leading-snug">{n.title}</p>
                {n.body ? <p className="text-xs text-gray-500 mt-0.5">{n.body}</p> : null}
              </button>
            ))}
          </div>
          <Link
            to="/admin/notifications"
            onClick={() => setOpen(false)}
            className="block text-center text-sm font-semibold py-2.5 text-[#64ba30] bg-[#f9fafb]"
          >
            Ver historial
          </Link>
        </div>,
        document.body,
      )}
    </div>
  )
}
